import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import styles from './Sidebar.module.css';

export interface SidebarProps {
  collapsed: boolean;
  open?: boolean;
}

interface NavItem {
  label: string;
  path: string;
  icon: string;
  moduleName?: string;
}

interface NavGroup {
  section: string | null;
  items: NavItem[];
}

// Navigation items configuration with module name mapping
const NAV_ITEMS: NavGroup[] = [
  {
    section: null,
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: '🏠', moduleName: 'Dashboard' },
    ],
  },
  {
    section: 'MASTERS',
    items: [
      { label: 'Party Master', path: '/dashboard/party-master', icon: '👥', moduleName: 'Party Master' },
      { label: 'Party Accounts', path: '/dashboard/party-accounts', icon: '💼', moduleName: 'Party Accounts' },
      { label: 'Token Master', path: '/dashboard/token-master', icon: '🎫', moduleName: 'Token Master' },
      { label: 'Dollar Accounts', path: '/dashboard/dollar-accounts', icon: '💵', moduleName: 'Dollar Account' },
      { label: 'Internal Accounts', path: '/dashboard/internal-accounts', icon: '🏦', moduleName: 'Internal Account' },
      { label: 'Payment Modes', path: '/dashboard/payment-modes', icon: '💳', moduleName: 'Payment Mode' },
    ],
  },
  {
    section: 'TRANSACTIONS',
    items: [
      { label: 'Buy', path: '/dashboard/buy', icon: '📥', moduleName: 'Buy' },
      { label: 'Sell', path: '/dashboard/sell', icon: '📤', moduleName: 'Sell' },
      { label: 'Swap', path: '/dashboard/swap', icon: '🔄', moduleName: 'Swap' },
    ],
  },
  {
    section: 'REPORTS',
    items: [
      { label: 'Ledger', path: '/dashboard/ledger', icon: '📋', moduleName: 'Ledger' },
      { label: 'Daily Closing', path: '/dashboard/daily-closing', icon: '📅', moduleName: 'Daily Closing' },
      { label: 'Reports', path: '/dashboard/reports', icon: '📊', moduleName: 'Reports' },
      { label: 'Audit Logs', path: '/dashboard/audit-logs', icon: '📋', moduleName: 'Audit Log' },
    ],
  },
  {
    section: 'ADMIN',
    items: [
      { label: 'User Management', path: '/dashboard/users', icon: '👤', moduleName: 'Users' },
    ],
  },
];

const Sidebar: React.FC<SidebarProps> = ({ collapsed, open = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Check if user has view permission for a module
  const hasModulePermission = (moduleName?: string): boolean => {
    if (!moduleName || !user?.userPermissionList) {
      return true; // Show by default if no module name or no permissions list
    }
    
    // Debug logging
    console.log(`Checking permission for module: ${moduleName}`);
    console.log('User permissions:', user.userPermissionList);
    
    const permission = user.userPermissionList.find(
      (p) => p.moduleName?.toLowerCase() === moduleName.toLowerCase()
    );
    
    console.log(`Permission found for ${moduleName}:`, permission);
    
    return permission?.isView === true;
  };

  const handleUserMenuToggle = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleProfileSettings = () => {
    navigate('/dashboard/profile-settings');
    setShowUserMenu(false);
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    logout();
  };

  // On mobile, show labels when sidebar is open; on desktop, show based on collapsed state
  const showLabels = open || !collapsed;

  return (
    <aside className={`${styles.sidebar} ${collapsed && !open ? styles.collapsed : ''} ${open ? styles.open : ''}`}>
      {/* Branding */}
      <div className={styles.branding}>
        {showLabels && (
          <>
            <h2 className={styles.brandingTitle}>Business Account</h2>
            <p className={styles.brandingSubtitle}>Management Software</p>
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className={styles.nav}>
        {NAV_ITEMS.map((group, groupIndex) => {
          // Filter items based on module permissions
          // If no permissions exist, show all items
          let visibleItems = group.items;
          
          if (user?.userPermissionList && user.userPermissionList.length > 0) {
            visibleItems = group.items.filter((item) => hasModulePermission(item.moduleName));
          } else {
            console.log('No userPermissionList found, showing all items');
          }
          
          // Skip group if no visible items
          if (visibleItems.length === 0) {
            return null;
          }

          return (
            <div key={groupIndex} className={styles.navGroup}>
              {group.section && showLabels && (
                <div className={styles.sectionLabel}>{group.section}</div>
              )}
              <ul className={styles.navList}>
                {visibleItems.map((item) => (
                  <li key={item.path} className={styles.navItem}>
                    <NavLink
                      to={item.path}
                      end={item.path === '/dashboard'}
                      className={({ isActive }) =>
                        `${styles.navLink} ${isActive ? styles.active : ''}`
                      }
                    >
                      <span className={styles.icon}>{item.icon}</span>
                      {showLabels && <span className={styles.label}>{item.label}</span>}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* User section with dropdown menu */}
      <div className={styles.userSection}>
        {user && (
          <>
            <button
              onClick={handleUserMenuToggle}
              className={styles.userButton}
              title="User menu"
            >
              <div className={styles.userAvatar}>{user.name.charAt(0).toUpperCase()}</div>
              {showLabels && (
                <div className={styles.userDetails}>
                  <div className={styles.userName}>{user.name}</div>
                  <div className={styles.userRole}>{user.role}</div>
                </div>
              )}
            </button>

            {/* User Menu Dropdown */}
            {showUserMenu && showLabels && (
              <div className={styles.userMenuDropdown}>
                <button
                  onClick={handleProfileSettings}
                  className={styles.menuItem}
                >
                  <span className={styles.menuIcon}>⚙️</span>
                  <span className={styles.menuLabel}>Profile Settings</span>
                </button>
                <button
                  onClick={handleLogout}
                  className={`${styles.menuItem} ${styles.logoutItem}`}
                >
                  <span className={styles.menuIcon}>🚪</span>
                  <span className={styles.menuLabel}>Sign Out</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
